import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../utils/firebase';
import { collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
// import Logo from '../components/Logo'; // Removed unused import
import TeamNavbar from '../components/TeamNavbar';

const TeamLeaderboards = () => {
  // const navigate = useNavigate(); // Removed unused variable
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userTeam, setUserTeam] = useState(null);

  const loadLeaderboards = useCallback(async () => {
    try {
      // Check cache first (24 hour cache for maximum cost efficiency)
      const cached = localStorage.getItem('teamLeaderboards');
      if (cached) {
        const cachedData = JSON.parse(cached);
        if (Date.now() - cachedData.timestamp < 86400000) { // 24 hours
          setTeams(cachedData.data);
          setLoading(false);
          return;
        }
      }

      // Load top 50 teams by GPA
      const teamsQuery = query(
        collection(db, 'teams'),
        orderBy('gpa', 'desc'),
        limit(50)
      );
      const teamsSnapshot = await getDocs(teamsQuery);
      const teamsData = teamsSnapshot.docs.map((doc, index) => ({
        id: doc.id,
        rank: index + 1,
        ...doc.data()
      }));

      // Cache the results
      localStorage.setItem('teamLeaderboards', JSON.stringify({
        data: teamsData,
        timestamp: Date.now()
      }));

      setTeams(teamsData);
    } catch (error) {
      console.error('Error loading leaderboards:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserTeam = useCallback(async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      
      if (userData?.teamId) {
        const teamDoc = await getDoc(doc(db, 'teams', userData.teamId));
        if (teamDoc.exists()) {
          setUserTeam(teamDoc.data());
        }
      }
    } catch (error) {
      console.error('Error loading user team:', error);
    }
  }, [user]);

  const getLastUpdated = () => {
    const cached = localStorage.getItem('teamLeaderboards');
    if (cached) {
      const cachedData = JSON.parse(cached);
      const hoursAgo = Math.floor((Date.now() - cachedData.timestamp) / 3600000);
      if (hoursAgo < 1) return 'Less than an hour ago';
      if (hoursAgo < 24) return `${hoursAgo} hour${hoursAgo > 1 ? 's' : ''} ago`;
      const daysAgo = Math.floor(hoursAgo / 24);
      return `${daysAgo} day${daysAgo > 1 ? 's' : ''} ago`;
    }
    return 'Never';
  };

  useEffect(() => {
    if (user) {
      loadLeaderboards();
      loadUserTeam();
    }
  }, [user, loadLeaderboards, loadUserTeam]);

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading leaderboards...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Team Navigation Bar */}
      <TeamNavbar />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* User Team Highlight */}
          {userTeam && (
            <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Your Team: {userTeam.name}</h2>
                  <div className="flex items-center space-x-6 text-white">
                    <div>
                      <span className="text-3xl font-bold">{userTeam.gpa?.toFixed(2) || '0.00'}</span>
                      <div className="text-sm opacity-90">GPA</div>
                    </div>
                    <div>
                      <span className="text-3xl font-bold">#{userTeam.ranking || 'N/A'}</span>
                      <div className="text-sm opacity-90">Global Rank</div>
                    </div>
                    <div>
                      <span className="text-3xl font-bold">{userTeam.gamesPlayed || 0}</span>
                      <div className="text-sm opacity-90">Games</div>
                    </div>
                  </div>
                </div>
                <div className="text-6xl">🏆</div>
              </div>
            </div>
          )}

          {/* Leaderboard */}
          <div className="bg-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Global Rankings</h2>
              <div className="text-sm text-gray-400">
                Updated daily • Last updated: {getLastUpdated()}
              </div>
            </div>

            <div className="space-y-3">
              {teams.map((team) => (
                <div 
                  key={team.id} 
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    userTeam?.id === team.id 
                      ? 'border-green-500 bg-green-900/20' 
                      : 'border-gray-700 bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="text-2xl font-bold text-yellow-500 w-12">
                      {getRankIcon(team.rank)}
                    </div>
                    <div>
                      <h3 className="font-bold text-white">{team.name}</h3>
                      <p className="text-gray-400 text-sm">League ID: {team.leagueId}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-500">{team.gpa?.toFixed(2) || '0.00'}</div>
                      <div className="text-gray-400 text-sm">GPA</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-500">{team.gamesPlayed || 0}</div>
                      <div className="text-gray-400 text-sm">Games</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-purple-500">{team.totalPoints || 0}</div>
                      <div className="text-gray-400 text-sm">Points</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {teams.length === 0 && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏆</div>
                <h3 className="text-xl font-bold text-white mb-2">No Teams Yet</h3>
                <p className="text-gray-400">Be the first to create a team and start competing!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamLeaderboards; 